import { useEffect, useState } from "react";
import { Copy, Download, FileSpreadsheet, Share } from "lucide-react";
import { api, type FileOffer } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Notice, Sheet } from "./ui";

/** Browsers inside other apps (WeChat, QQ, Weibo, Facebook, Instagram, LINE…) can't save files at all. */
function inAppBrowser(): "wechat" | "other" | null {
  const ua = navigator.userAgent;
  if (/MicroMessenger/i.test(ua)) return "wechat";
  if (/QQ\/|Weibo|FBAN|FBAV|Instagram|Line\/|DingTalk|AlipayClient/i.test(ua)) return "other";
  return null;
}

/**
 * On a phone, an exported file (Excel) is offered here once it is ready, two ways: "save or send" opens the
 * phone's own menu (save to Files, open in Excel or Numbers, send by mail or chat), and "download" is a plain
 * link to the same file, which the browser saves by itself (on an iPhone: in Files › Downloads). Phones only
 * allow either straight from a tap, which is why the file can't simply download by itself.
 */
export function FileReady() {
  const { t } = useI18n();
  const [offer, setOffer] = useState<FileOffer | null>(null);
  const [err, setErr] = useState<string | null>(null);
  /** A short-lived link to the file that opens in its own window — with a "Done" button on an iPhone. */
  const [href, setHref] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onReady = (e: Event) => {
      const o = (e as CustomEvent<FileOffer>).detail;
      setErr(null);
      setHref(null);
      setCopied(false);
      setOffer(o);
      api
        .post<{ href: string }>("/api/files/link", { path: o.url })
        .then((r) => setHref(r.href))
        .catch(() => setHref(null));
    };
    window.addEventListener("nc:file-ready", onReady);
    return () => window.removeEventListener("nc:file-ready", onReady);
  }, []);

  const file = offer?.file ?? null;
  const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
  let canShare = false;
  try {
    canShare = !!file && typeof navigator.share === "function" && (!nav.canShare || nav.canShare({ files: [file] }));
  } catch {
    canShare = false;
  }
  const inApp = offer ? inAppBrowser() : null;

  async function copyLink() {
    if (!href) return;
    const full = new URL(href, location.origin).href;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
    } catch {
      // No clipboard here: show the link so it can be pressed and copied by hand.
      window.prompt(t("file.copyLink"), full);
    }
  }

  async function share() {
    if (!file) return;
    try {
      // Only the file: a title or text next to it makes some iPhone apps save a text note instead.
      await navigator.share({ files: [file] });
      setOffer(null);
    } catch (e) {
      // Closing the menu without choosing is fine; anything else is said, with the way that still works.
      const name = (e as Error)?.name ?? "";
      if (name !== "AbortError") setErr(name || "Error");
    }
  }

  return (
    <Sheet open={!!offer} onClose={() => setOffer(null)} title={t("file.ready")}>
      <div className="row" style={{ alignItems: "center", gap: 12 }}>
        <FileSpreadsheet size={36} aria-hidden="true" style={{ flex: "none", color: "var(--basil)" }} />
        <strong style={{ wordBreak: "break-all" }}>{file?.name}</strong>
      </div>
      {inApp && <Notice tone="warn">{t(inApp === "wechat" ? "file.inWechat" : "file.inApp")}</Notice>}
      {/* Inside WeChat & co. nothing can be saved: the download link (no sign-in needed, 10 minutes) goes to a real browser. */}
      {inApp && href && (
        <button type="button" className="btn block" onClick={copyLink}>
          <Copy size={18} /> {t("file.copyLink")}
        </button>
      )}
      {inApp && href && <p className="small muted">{copied ? t("file.copied") : t("file.copyHint")}</p>}
      {canShare && (
        <button type="button" className="btn block" onClick={share}>
          <Share size={18} /> {t("file.share")}
        </button>
      )}
      {offer && (
        // Opens in a new window: on an iPhone that's a viewer with "Done" to come back, and "Share" or
        // "More…" to save the file in Files or open it in Excel / Numbers.
        <a
          href={href ?? offer.url}
          target="_blank"
          rel="noopener"
          className={`btn block ${canShare ? "secondary" : ""}`}
          onClick={() => setTimeout(() => setOffer(null), 800)}
        >
          <Download size={18} /> {t("file.download")}
        </a>
      )}
      {!inApp && <p className="small muted">{t(canShare ? "file.hint" : /Android/i.test(navigator.userAgent) ? "file.hintAndroid" : "file.hintDownload")}</p>}
      {err && <Notice tone="warn">{t("file.failed", { code: err })}</Notice>}
    </Sheet>
  );
}
